import { Inject, Options, Prop, Vue, Watch } from 'vue-property-decorator';
import AddBreachesModal from './add-breaches-modal/index.vue';
import { TrustAPI } from '@/shared/modules/trust-api';
import { FormEERData } from '@/trust-atcs-app/models/form-eer-data';
import { AppInsights } from '@/trust-atcs-app/appInsights';
import { EventEmitter } from 'events';
import store from '@/trust-atcs-app/store';
import { EeReport, EeReportBreach } from '@/shared/modules/typescript-trustapi-client/models';
import { orderBy } from 'lodash';

@Options({
  components: {
    AddBreachesModal,
  },
  emits: ['update:isSectionLoading', 'validForm'],
})
export default class FormEerBreaches extends Vue {
  @Prop({ default: false })
  private isSectionLoading!: boolean;

  @Prop({ default: {} })
  private formEERData!: FormEERData;

  @Watch('loadingContent')
  private sectionLoading() {
    this.$emit('update:isSectionLoading', this.loadingContent);
  }

  private ai = new AppInsights();
  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private loadingContent = false;
  private errorLoadingContent = false;

  private breaches: any = {};
  private breachesListDisplay: any[] = [];

  @Watch('selectedOption')
  private onSelectedOptionChanged() {
    this.breaches.hasBreaches = this.selectedOption == 1;
    // Don't reset the breaches list if the user has already added breaches
    if (!this.breaches.hasBreaches) {
      this.breaches.breaches = [];
      this.breachesListDisplay = [];
      this.breaches.breachesComment = '';
      this.eventCtrl.emit('save-current');
    }
    this.checkCanGoNext();
  }

  private newWording =
    'Have there been breaches of legislation? If yes, please search and select the relevant legislation.';
  private selectedOption = 0;

  private showAddBreachesModal = false;

  private tableHeadings = [
    { text: 'Type', value: 'breachType', sortable: false, sortMethod: 'asc' },
    { text: 'Breach code', value: 'breachCode', sortable: true, sortMethod: 'acs' },
    { text: 'Legislation', value: 'legislation', sortable: true, sortMethod: 'asc' },
    { text: 'Description', value: 'description', sortable: false, sortMethod: 'acs' },
    { text: 'Action', value: '', sortable: false, sortMethod: 'acs' },
  ];

  private activeSortHeader = 1;
  private sortMethod = 'desc';

  private sortTableColumn(index: number, sortable: boolean, sortDirection?: string): void {
    if (sortable) {
      this.breachesListDisplay = this.breaches.breaches;
      if (this.tableHeadings[index].sortable) {
        this.activeSortHeader = index;
        this.sortMethod = sortDirection ? sortDirection : this.sortMethod === 'desc' ? 'asc' : 'desc';
        this.tableHeadings[index].sortMethod = this.sortMethod;
        if (this.breachesListDisplay.length > 0) {
          const nameSorter =
            index == 0
              ? (c: any) => c.description.toLowerCase()
              : [this.tableHeadings[index].value, (c: any) => c.description.toLowerCase()];
          const orderByResults = orderBy(this.breachesListDisplay, nameSorter, [this.sortMethod as any]);
          this.breachesListDisplay = orderByResults;
        }
      }
    }
  }

  mounted(): void {
    this.eventCtrl.on('reload-form-eer-breaches-content', this.loadBreachesContent);
    this.loadBreachesContent();
    this.$emit('validForm', false);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-form-eer-breaches-content', this.loadBreachesContent);
  }

  private async loadBreachesContent(): Promise<void> {
    try {
      this.loadingContent = true;
      const resp: any = await this.apiService.EeReportApi?.apiV2EeReportGetBreachesPost({
        complianceFormId: this.complianceFormEER.id,
        updatingKey: this.updatingKey,
      });
      this.breaches = resp.data.data as EeReportBreach[];
      if (this.breaches.breaches == null) {
        this.breaches.breaches = [];
      }
      this.formEERData.breaches = this.breaches as any;
      if (this.formEERData.breaches?.hasBreaches == null) {
        this.selectedOption = 0;
      } else {
        this.selectedOption = this.formEERData.breaches?.hasBreaches ? 1 : 2;
      }
      this.sortTableColumn(this.activeSortHeader, true, 'asc');
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Error getting Breaches list');
      this.ai.trackException('Error getting Breaches list: ' + err);
      this.errorLoadingContent = true;
      console.log(err);
    } finally {
      this.loadingContent = false;
    }
  }

  private checkCanGoNext(): boolean {
    const isYes =
      this.selectedOption == 1 &&
      this.breaches.breaches?.length > 0 &&
      this.breaches.breachesComment?.trim().length > 0;
    const isNo = this.selectedOption == 2;
    const validForm = isYes || isNo;
    this.$emit('validForm', validForm);
    return validForm;
  }

  private addBreaches() {
    this.sortTableColumn(this.activeSortHeader, true, 'asc');
    this.eventCtrl.emit('save-section', false, true, false);
    this.checkCanGoNext();
  }

  private isRemoving = false;

  private removeBreach(id: number) {
    this.isRemoving = true;

    // remove the breach from the list
    setTimeout(() => {
      this.breaches.breaches = this.breaches.breaches.filter((b: any) => {
        return b.id != id;
      });
      this.breachesListDisplay = this.breachesListDisplay.filter((b: any) => {
        return b.id != id;
      });
      this.sortTableColumn(this.activeSortHeader, true, 'asc');
      this.eventCtrl.emit('save-current');
      this.checkCanGoNext();
      this.isRemoving = false;
    }, 500);
  }

  get complianceFormEER(): EeReport {
    return store.getters.getSelectedComplianceFormEER;
  }

  get updatingKey(): string {
    return store.getters.getFormUpdatingKey;
  }
}
